import { FormValueScope, RadioButtonField } from '@epages/react-components'
import type { FunctionComponent, PropsWithChildren, ReactElement } from 'react'

import { RangeSliderWithInput } from '../templateComponents/Workspace/plugins/components/ImageTextColorSettings'
import CheckboxField from '../formComponents/CheckboxField'
import ColorField from '../formComponents/ColorField'
import CustomCssField from '../formComponents/CustomCssField'
import FontField from '../formComponents/FontField'
import FormGroup from '../formComponents/FormGroup'
import FormValue from '../formComponents/FormValue'
import InputField from '../formComponents/InputField'
import SelectableInputField from '../formComponents/SelectableInputField'

type SettingsFieldProps = {
  scopedName?: string
  setting: ImmutableMap
  defaultThemeSettings: ImmutableMap
  // Extra prop that is used with "Custom CSS" handling:
  onCustomCssInfoText?: () => void
  // Extra prop used by the RadioButton settings
  disabled?: boolean
  // Extra prop for InputSetting
  parent?: ImmutableMap
  // Extra prop for GroupSetting
  isTopLevel?: boolean
} & TranslateProps

export const settingsFieldMap: {
  [key: string]: FunctionComponent<PropsWithChildren<Readonly<SettingsFieldProps>>>
} = {
  group: GroupSettingField,
  selection: SelectionSettingField,
  input: InputSettingField,
  checkbox: CheckboxSettingField,
  color: ColorSettingField,
  font: FontSettingField,
  customCss: CustomCssSettingField,
  radioButtons: RadioButtonsSettingField,
  rangeWithInput: RangeWithInput,
}

function GroupSettingField({
  t,
  setting,
  scopedName,
  isTopLevel,
  ...props
}: Readonly<SettingsFieldProps>): ReactElement {
  return (
    <div className="ep-theme-settings-group" key={setting.get('name')}>
      {!isTopLevel && <h4 className="ep-theme-settings-group-heading">{t(setting.get('name'))}</h4>}
      {setting.has('description') && (
        <p className="ep-theme-settings-group-description">{t(setting.get('description'))}</p>
      )}
      <FormValueScope name={setting.get('name')}>
        <>
          {setting.get('settings').map((groupSetting: ImmutableMap) =>
            settingsFieldMap[groupSetting.get('type')]({
              setting: groupSetting,
              scopedName: scopedName ? `${scopedName}.${setting.get('name')}` : setting.get('name'),
              t,
              ...props,
            }),
          )}
        </>
      </FormValueScope>
    </div>
  )
}

// For now, the only possible "options" are "input" and "checkbox".
function SelectionSettingField({ setting, ...rest }: Readonly<SettingsFieldProps>): ReactElement {
  return (
    <div key={setting.get('name')}>
      <FormValueScope name={setting.get('name')}>
        <>
          {setting.get('options').map((option: ImmutableMap) =>
            settingsFieldMap[option.get('type')]({
              setting: option,
              parent: setting,
              ...rest,
            }),
          )}
        </>
      </FormValueScope>
    </div>
  )
}

function InputSettingField({ t, setting, parent }: Readonly<SettingsFieldProps>): ReactElement {
  const isSelection = parent?.get('type') === 'selection'
  const name: string = setting.get('name')

  const selectableInput = (
    <div className="ep-form-row" key={name}>
      <SelectableInputField name={name} label={t(name)} />
    </div>
  )

  const input = (
    <FormGroup key={name} name={name} tagName="label" label={t(name)}>
      <InputField name={name} />
    </FormGroup>
  )

  return isSelection ? selectableInput : input
}

function CheckboxSettingField({ t, setting }: Readonly<SettingsFieldProps>): ReactElement {
  const name: string = setting.get('name')

  return (
    <FormGroup key={name} name={name} tagName="label" label={t(name)} labelAfterComponent>
      <CheckboxField name={name} />
    </FormGroup>
  )
}

function ColorSettingField({
  t,
  setting,
  scopedName,
  defaultThemeSettings,
}: Readonly<SettingsFieldProps>): ReactElement {
  const name: string = setting.get('name')

  return (
    <FormGroup labelAfterComponent key={name} name={name} label={t(name)} className="ep-color-field-item">
      <ColorField
        name={name}
        side="left"
        className="ep-color-field-box"
        resetValue={defaultThemeSettings.getIn(`${scopedName}.${name}`.split('.'))}
      />
    </FormGroup>
  )
}

function FontSettingField({
  t,
  setting,
  scopedName,
  defaultThemeSettings,
}: Readonly<SettingsFieldProps>): ReactElement {
  const name: string = setting.get('name')

  return (
    <FormGroup key={name} name={name} label={t(name)} labelClassName="ep-font-style">
      <FontField
        fonts={setting.get('fonts')}
        resetValue={defaultThemeSettings.getIn(`${scopedName}.${name}`.split('.'))}
      />
    </FormGroup>
  )
}

function CustomCssSettingField({
  setting,
  scopedName,
  defaultThemeSettings,
  onCustomCssInfoText,
}: Readonly<SettingsFieldProps>): ReactElement {
  const name: string = setting.get('name')

  return (
    <div className="ep-form-row" key={name}>
      <CustomCssField
        name={name}
        resetValue={defaultThemeSettings.getIn(`${scopedName}.${name}`.split('.'))}
        onInfoText={onCustomCssInfoText}
      />
    </div>
  )
}

function RadioButtonsSettingField({ t, setting, scopedName, disabled }: Readonly<SettingsFieldProps>): ReactElement {
  const name: string = setting.get('name')
  const buttons = setting.get('options').map((option) => ({
    value: option,
    label: t(`${name}.options.${option}`),
  }))

  return (
    <FormGroup
      name={name}
      className={disabled ? 'disabled' : ''}
      key={`${scopedName}.${name}`}
      label={t(`${name}.label`)}
    >
      <RadioButtonField
        className="dali-settingslayer-logo-alignment"
        name={name}
        buttons={buttons.toJS()}
        disabled={disabled}
      />
    </FormGroup>
  )
}

function RangeWithInput({ t, setting, scopedName, disabled }: Readonly<SettingsFieldProps>): ReactElement {
  const name = setting.get('name')

  return (
    <FormValue name={name} key={`${scopedName}.${name}`}>
      {(value: number, setValue: (newValue: number) => void) => (
        <RangeSliderWithInput label={t(`${name}.label`)} {...{ name, value, setValue, disabled }} />
      )}
    </FormValue>
  )
}

export default function themeSettingsFields(
  t: TranslateProps['t'],
  defaultThemeSettings: ImmutableMap,
  extraProps?: Record<string, any>,
): Record<
  string,
  FunctionComponent<PropsWithChildren<Omit<Readonly<SettingsFieldProps>, 't' | 'defaultThemeSettings'>>>
> {
  return Object.entries(settingsFieldMap).reduce<
    Record<string, FunctionComponent<Omit<Readonly<SettingsFieldProps>, 't' | 'defaultThemeSettings'>>>
  >(
    (fields, [name, settingField]) => ({
      [name]: (props) => settingField({ t, defaultThemeSettings, ...props, ...extraProps }),
      ...fields,
    }),
    {},
  )
}
